const BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001'
// For the one thing api() cannot do: an <img> pointed at an app path (a profile photo, utils/storedFiles mediaSrc).
export const API_BASE = BASE

export class ApiError extends Error {
  constructor(status, body) {
    super(body?.error || body?.message || `Request failed with status ${status}`)
    this.name = 'ApiError'
    this.status = status
    this.body = body
  }
}

// ── Account slots (multi-account; matches backend Block 193) ──────────────────
// The browser can hold up to MAX_ACCOUNT_SLOTS signed-in accounts, each with its
// own refresh cookie (server-side) and its own stored access token (here). Slot 0
// is the legacy/first account: it falls back to the old 'aidey_at' key and sends
// no distinct behaviour, so an existing single-account user is byte-identical.
export const MAX_ACCOUNT_SLOTS = 2
const ACCOUNTS_KEY = 'aidey_accounts'
const ACTIVE_SLOT_KEY = 'aidey_active_slot'

function atKey(slot) { return `aidey_at_${slot}` }

function readActiveSlot() {
  try {
    const n = parseInt(localStorage.getItem(ACTIVE_SLOT_KEY), 10)
    return Number.isInteger(n) && n >= 0 && n < MAX_ACCOUNT_SLOTS ? n : 0
  } catch { return 0 }
}
function readStoredToken(slot) {
  if (typeof sessionStorage === 'undefined') return null
  // Slot 0 falls back to the legacy 'aidey_at' key so a pre-slotting session boots unchanged.
  return sessionStorage.getItem(atKey(slot)) || (slot === 0 ? sessionStorage.getItem('aidey_at') : null)
}

let activeSlot = readActiveSlot()
let accessToken = readStoredToken(activeSlot)
let activeWorkspaceId = null

export function getActiveSlot() { return activeSlot }
export function setActiveSlot(n) {
  activeSlot = Number.isInteger(n) && n >= 0 && n < MAX_ACCOUNT_SLOTS ? n : 0
  try { localStorage.setItem(ACTIVE_SLOT_KEY, String(activeSlot)) } catch {}
  // Point the in-memory token at the new slot's stored token (may be null → a
  // refresh will mint it). Keeps the Bearer token consistent with the slot header.
  accessToken = readStoredToken(activeSlot)
}

export function setActiveWorkspaceId(id) { activeWorkspaceId = id }
export function getActiveWorkspaceId() { return activeWorkspaceId }

export function setAccessToken(t) {
  accessToken = t
  if (typeof sessionStorage !== 'undefined') {
    if (t) {
      sessionStorage.setItem(atKey(activeSlot), t)
      if (activeSlot === 0) sessionStorage.removeItem('aidey_at') // retire the legacy key once migrated
      // Durable hint that a session exists — survives page refresh (unlike sessionStorage).
      if (typeof localStorage !== 'undefined') localStorage.setItem('aidey_session_hint', '1')
    } else {
      sessionStorage.removeItem(atKey(activeSlot))
      if (activeSlot === 0) sessionStorage.removeItem('aidey_at')
      // Only drop the durable hint when NO account remains, so a switch/remove
      // that leaves another account signed in still boots straight into it.
      if (typeof localStorage !== 'undefined' && getAccounts().length === 0) {
        localStorage.removeItem('aidey_session_hint')
      }
    }
  }
}
export function getAccessToken() { return accessToken }

// ── Client-side slot registry ─────────────────────────────────────────────────
// The ONLY record that a browser holds a given account. There is NO server-side
// link between the accounts — this list lives here and nowhere else. If it is
// cleared or corrupted, the active session's cookie still works (a refresh boots
// it), and the switcher simply shows one account until others are re-added.
export function getAccounts() {
  try {
    const raw = JSON.parse(localStorage.getItem(ACCOUNTS_KEY) || '[]')
    return Array.isArray(raw)
      ? raw.filter(a => a && Number.isInteger(a.slot) && a.slot >= 0 && a.slot < MAX_ACCOUNT_SLOTS)
      : []
  } catch { return [] }
}
function saveAccounts(list) {
  try { localStorage.setItem(ACCOUNTS_KEY, JSON.stringify(list)) } catch {}
}
export function upsertAccount(entry) {
  const list = getAccounts().filter(a => a.slot !== entry.slot)
  list.push({ slot: entry.slot, userId: entry.userId, label: entry.label || '' })
  saveAccounts(list)
  return list
}
export function removeAccountEntry(slot) {
  const list = getAccounts().filter(a => a.slot !== slot)
  saveAccounts(list)
  try { sessionStorage.removeItem(atKey(slot)) } catch {}
  try { localStorage.removeItem(`aidey_active_workspace_${slot}`) } catch {}
  return list
}
export function nextFreeSlot() {
  const used = new Set(getAccounts().map(a => a.slot))
  for (let s = 0; s < MAX_ACCOUNT_SLOTS; s++) if (!used.has(s)) return s
  return -1 // full
}

// Stable device fingerprint — persists in localStorage so the same browser
// always sends the same fingerprint, enabling session deduplication on the server.
function getDeviceFingerprint() {
  if (typeof localStorage === 'undefined') return null
  let fp = localStorage.getItem('aidey_device_fp')
  if (!fp) {
    fp = crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`
    localStorage.setItem('aidey_device_fp', fp)
  }
  return fp
}

// Single shared promise — all concurrent 401s wait on one refresh call instead of
// each firing independently and racing to rotate the refresh token.
let refreshPromise = null

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise

  // Capture whether a session was believed to exist before this call — used to
  // gate aidey:auth:expired so anonymous visitors don't trigger logout side
  // effects when their boot-time refresh returns 401.
  // Kept for event-gating even though it no longer short-circuits the call, so
  // every browser with a valid httpOnly cookie gets a recovery attempt.
  const hadSessionHint =
    !!accessToken ||
    (typeof localStorage !== 'undefined' && !!localStorage.getItem('aidey_session_hint'))

  async function doRefresh(isRetry) {
    try {
      const res = await fetch(`${BASE}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Device-Fingerprint': getDeviceFingerprint() || '',
          'X-Account-Slot': String(activeSlot),
        },
      })
      if (res.ok) {
        const data = await res.json()
        setAccessToken(data.accessToken)
        return data
      }
      setAccessToken(null)
      if (hadSessionHint && typeof window !== 'undefined') {
        window.dispatchEvent(new Event('aidey:auth:expired'))
      }
      return false
    } catch (_) {
      if (!isRetry) {
        await new Promise(r => setTimeout(r, 1500))
        return doRefresh(true)
      }
      return 'network-error'
    }
  }

  refreshPromise = doRefresh(false).finally(() => { refreshPromise = null })
  return refreshPromise
}

export async function api(path, options = {}) {
  // _authRetried: this call is already the one retry after a refresh (see the 401 branch below).
  // Kept out of what fetch receives.
  const { _authRetried, ...fetchOptions } = options
  options = fetchOptions
  const fp = getDeviceFingerprint()
  const headers = {
    ...(options.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(fp ? { 'X-Device-Fingerprint': fp } : {}),
    ...(activeWorkspaceId ? { 'X-Active-Workspace': activeWorkspaceId } : {}),
    // Which account slot this request belongs to. A caller can override per-request
    // (e.g. signing into a not-yet-active slot) by passing it in options.headers.
    'X-Account-Slot': String(activeSlot),
    ...options.headers,
  }

  const res = await fetch(`${BASE}${path}`, {
    ...options,
    headers,
    credentials: 'include',
  })

  if (res.status === 401 && path !== '/api/auth/refresh') {
    // Refresh the token and retry ONCE. 2026-10-08, live on /aidey: this retried by calling api()
    // again with nothing to stop it, so a request the server kept refusing after a successful
    // refresh looped refresh -> retry -> refresh for ever (about 4,900 requests in 10 seconds in
    // the preview), and the chat history drew its empty state while it did.
    if (!_authRetried) {
      const refreshed = await refreshAccessToken()
      if (refreshed && refreshed !== 'network-error') {
        return api(path, { ...options, _authRetried: true })
      }
    }
    // Session expired — auth handler already fired aidey:auth:expired; mark
    // this error so the global unhandledrejection handler skips the toast.
    const body401 = await res.json().catch(() => ({}))
    const authErr = new ApiError(401, body401)
    authErr.isAuthError = true
    throw authErr
  }

  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    if (res.status === 403 && body?.code === 'PERSONAL_SUBSCRIPTION_REQUIRED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('aidey:subscription:required'))
      }
    }
    // ACCOUNT-4: access enforcement. A blocked user gets 402 on every non-exempt
    // route. Refresh currentUser (via the exempt /refresh path) so its enforcing
    // verdict flips and the paywall renders; mark the error handled so the global
    // rejection handler doesn't also surface a toast.
    if (res.status === 402 && body?.code === 'ACCESS_BLOCKED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('aidey:access:blocked', { detail: { reason: body?.reason || null } }))
      }
      const blockedErr = new ApiError(402, body)
      blockedErr.isAccessBlocked = true
      throw blockedErr
    }
    // Card at sign-up (phase 2): an account that has not finished starting its trial gets 402
    // CARD_SETUP_REQUIRED from everything except signing in and the trial routes. Refresh
    // currentUser so App shows "Finish starting your free trial"; never a toast for it.
    if (res.status === 402 && body?.code === 'CARD_SETUP_REQUIRED') {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new Event('aidey:card:required'))
      }
      const cardErr = new ApiError(402, body)
      cardErr.isCardSetupRequired = true
      throw cardErr
    }
    throw new ApiError(res.status, body)
  }

  return res
}
